import { useEffect, useState } from "react";
import { isPlatformNodeJS } from "../utils/platform.is-node";


/**
 * integrates media queries into React hooks, returning a boolean which
 * constantly will reflect whether or not the current media query is matched
 *
 * note that during SSR (and initial render) this hook will return undefined,
 * as media queries do not make sense in a server-side context
 *
 * if options.immediate is set to true, then the matching of the mediaQuery
 * will be applied immediately. This means that inconsistensies between client
 * and server WILL happen and is such this argument should ever only be used
 * inside code that is exclusively executed on the client.
 */
export function useMatchMedia(
  mediaQuery: string,
  options?: { immediate?: boolean },
): boolean | undefined {
  const [isMatching, setMatching] = useState<boolean | undefined>(
    options?.immediate && !isPlatformNodeJS()
      ? window.matchMedia?.(mediaQuery).matches
      : undefined,
  );

  useEffect(() => {
    const MQL = window.matchMedia?.(mediaQuery);

    const handleChange = ({ matches }: MediaQueryListEvent) => {
      setMatching(matches);
    };

    setMatching(MQL?.matches ?? false);
    MQL?.addEventListener("change", handleChange);

    return () => {
      MQL?.removeEventListener("change", handleChange);
    };
  }, [mediaQuery]);

  return isMatching;
}
